<template>
    <Transition name="loader">
        <div class="global-loader" v-show="loader.isActive">
            <img class="loader-img" v-bind="images.loader" />
        </div>
    </Transition>

    <component :is="layout" :prop-alert="alert" :prop-is-ready="isUserDataLoaded">
        <router-view :key="$route.fullPath"></router-view>
    </component>
</template>

<style></style>

<script lang="ts">
import { computed, defineComponent, inject } from 'vue';
import { mapStores, storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useAppStore, useUserStore, useActionStore } from '@coreStores/index';
import { useGeneral } from '@app/base-package';
import type { IImages } from '@coreInterfaces/interface';
import urls from '@assets/url/urls.json';

export default defineComponent({
    name: 'AppLayout',

    data () {
        return {
            alert: {
                type: '',
                title: '',
                message: ''
            } as {
                type: string;
                title: string;
                message: string;
            },
            images: {
                loader: {
                    src: '../images/loader/loader.svg',
                    alt: 'loader'
                }
            } as IImages,
            isUserDataLoaded: false as boolean,
            skipUserData: [
                'trdLogin',
                'login',
                '404',
                '500',
                ''
            ] as Array<string>
        };
    },

    async setup () {
        // console.log('%c App Layout - setup', 'background: #222; color: #bada55');

        const appStore = useAppStore();
        const userStore = useUserStore();
        const actionStore = useActionStore();
        const router = useRouter();
        const getDeviceType = inject('getDeviceType') as Function;
        const { response } = storeToRefs(actionStore);
        const loader = appStore.getLoader();
        // const imgDomain = appStore.getEnvAttrsByKey('VITE_IMG_DOMAIN');

        /** Setting Before Load */
        appStore.setIsLoaded(false);
        appStore.setLoading(true);

        /** Setting B.O. Types */
        appStore.setTypes(['adm', 'stg', 'afl']);

        /** Setting Urls */
        type UrlKeys = keyof typeof urls;

        for (let key in urls) {
            actionStore.setUrl(key, appStore.getEnvAttrsByKey('VITE_APP_DOMAIN') + urls[key as UrlKeys]);
        }

        // await fetch(`../url/urls.json${appStore.getJSONVersion()}` as string)
        //     .then((response) => response.json())
        //     .then((urls) => {
        //         for (let key in urls) {
        //             actionStore.setUrl(key, appStore.getEnvAttrsByKey('VITE_APP_DOMAIN') + urls[key]);
        //         }
        //     })
        //     .catch((error) => {
        //         console.error('urls loaded with an error,', error);
        //     });

        /** Waiting Vue Router Get Ready */
        await router.isReady();

        const layout = computed((): string => {
            if (getDeviceType().isDesktop) {
                userStore.setDeviceMode('desktop');
                document.body.classList.add('desktop');
                return 'DesktopLayout';
            } else {
                userStore.setDeviceMode('mobile');
                document.body.classList.add('mobile');
                return 'MobileLayout';
            }
        });

        /** Setting After Load */
        appStore.setIsLoaded(true);

        return {
            appStore,
            userStore,
            actionStore,
            response,
            layout,
            loader
        };
    },

    mounted () {
    },

    methods: {
    },

    computed: {
        ...mapStores(useUserStore)
    },

    watch: {
        response (newValue, oldValue) {
            if (newValue.action) {
                if (this.userStore.getTestMode()) {
                    console.debug('Main Layout Response', newValue, oldValue);
                }

                if (newValue.message && !['ok', 'otp is expired.'].includes(newValue.message.toLowerCase())) {
                    let state = newValue.status ? 'success' : 'error';
                    useGeneral.onShowReponse(state, newValue.message);
                }

                if (newValue.message && newValue.message.toLowerCase() == 'ok' && !newValue.status) {
                    let state = newValue.status ? 'success' : 'error';
                    useGeneral.onShowReponse(state, newValue.error);
                }

                if (['logout', 'groupLogout', 'affiliateLogout'].includes(newValue.action)) {
                    let state = newValue.status ? 'success' : 'error';
                    useGeneral.onShowReponse(state, this.$t('logout_success'));
                }

                if (newValue.status == undefined) {
                    this.userStore.clearUserStore();
                    this.actionStore.clearComponentAction();
                    this.actionStore.clearError();
                    this.actionStore.clearFormError();
                }

                this.actionStore.resetResponse();
                this.appStore.setLoading(false);
            }
        },

        userStore: {
            immediate: true,
            deep: true,
            handler (newValue, oldValue) {
                if (this.skipUserData.includes(newValue.getPage())) {
                    this.isUserDataLoaded = true;
                } else {
                    if (newValue.getInfoByKey('id') != null) {
                        this.isUserDataLoaded = true;
                    }
                }
            }
        }
    }
});
</script>
